import React from 'react';
import { Alert, Box, CircularProgress, Paper, Typography } from 'components/mui';
import {
	buildCurrentSessionCopy,
	buildNextSessionCopy,
	ThemeSession,
} from '../libs/themeLists';
import { SessionLoadState } from '../hooks/useQuantumHealingSession';

interface NextSessionHeaderProps {
	state: SessionLoadState;
	/** The session running now, or else the next to start; null if neither. */
	session: ThemeSession | null;
	/** True when `session` is already running rather than still to come. */
	isCurrent: boolean;
	/** False when the schedule couldn't be read and sessions are assumed one day long. */
	endDatesKnown: boolean;
}

const bold = { fontWeight: 700 };

/**
 * Which session this is and where it stands — running now, or how far away.
 * Presentation only — the session is resolved by useQuantumHealingSession so
 * the roster shares the same fetch.
 */
function NextSessionHeader({ state, session, isCurrent, endDatesKnown }: NextSessionHeaderProps) {
	if (state === 'loading') {
		return (
			<Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
					<CircularProgress size={22} />
					<Typography color="text.secondary">
						Checking your upcoming session…
					</Typography>
				</Box>
			</Paper>
		);
	}

	if (state === 'error') {
		return (
			<Alert severity="error" sx={{ mb: 3 }}>
				We couldn&apos;t load your upcoming session. Please refresh the page — if
				it keeps happening, contact support.
			</Alert>
		);
	}

	if (!session) {
		return (
			<Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					No upcoming session scheduled
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					A session appears here once someone enrolls, or after you press Sync
					sessions for a newly published month.
				</Typography>
			</Paper>
		);
	}

	const copy = isCurrent ? buildCurrentSessionCopy(session) : buildNextSessionCopy(session);

	return (
		<>
			{!endDatesKnown && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					The session schedule couldn&apos;t be loaded, so each session is shown as
					lasting one day. Refresh to try again.
				</Alert>
			)}

			<Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
				<Typography variant="h6" sx={{ fontWeight: 400 }}>
					{copy.lead}
					<Box component="span" sx={bold}>
						{copy.emphasis}
					</Box>
					{copy.connector}
					<Box component="span" sx={bold}>
						{copy.dateLabel}
					</Box>
				</Typography>
			</Paper>

			<Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
				<Typography variant="h6" sx={{ fontWeight: 400 }}>
					Theme:{' '}
					<Box component="span" sx={bold}>
						{session.themeName}
					</Box>
				</Typography>
			</Paper>
		</>
	);
}

export default NextSessionHeader;
